import { Injectable, signal } from '@angular/core';
import { environment } from '@env';

export type Persona = 'vendor' | 'admin' | 'customer';

/** Derive the persona from the user's role codes (mirrors the API's ResolvePersonaAsync). */
export function personaFromRoles(roles: string[] | null | undefined): Persona | null {
  const r = (roles ?? []).map(x => (x ?? '').toLowerCase());
  if (r.includes('super_admin') || r.includes('tenant_admin')) return 'admin';
  if (r.includes('vendor_owner') || r.includes('vendor_staff')) return 'vendor';
  if (r.includes('customer')) return 'customer';
  return null;
}

/**
 * Trexa is a single-operator marketplace — there is NO tenant layer (unlike LMS).
 * Vendor-owned data is scoped by the current vendor (the mockup's `meId`), customer
 * data by the current customer; admin sees everything.
 */
@Injectable({ providedIn: 'root' })
export class PersonaService {
  private readonly STORAGE_KEY = 'trexa.persona.state';

  readonly currentPersona    = signal<Persona | null>(null);
  /** The logged-in vendor id (mockup's `meId`) — used to scope vendor-owned data. */
  readonly currentVendorId   = signal<number | null>(null);
  /** The logged-in customer id — used to scope customer-owned data. */
  readonly currentCustomerId = signal<number | null>(null);
  readonly currentUserId     = signal<number | null>(null);
  /**
   * The logged-in person's display name, for anything that has to record WHO did something —
   * the PDPA access log above all (`CustomerAccessLog.AccessedBy`).
   *
   * This replaces a `currentUser: UserPoco` signal that nothing ever wrote: neither `setPersona`
   * call site in `auth.service` passed `opts.user`, and `persist()` did not store it, so
   * `currentUser()?.name` was permanently null and all three of its readers fell through to their
   * `Admin #1` / `User #3` fallbacks. Every PII access-log row the app had ever written named an
   * internal id instead of a person — the one thing a PDPA accountability record must not do
   * (QA 2026-09-22). A plain string is also what the callers actually want: login returns an
   * `AuthUserDto` (`{ id, email, name }`), which is not a `UserPoco` and could never have been
   * assigned to that signal anyway.
   *
   * Persisted, because a reload must not silently downgrade the audit trail to an id.
   */
  readonly currentUserName   = signal<string>('');
  /** Role codes from the JWT (drives role-gated app chrome). */
  readonly roles             = signal<string[]>([]);
  /**
   * True while an admin is onboarding a vendor and temporarily acting as it to set up its
   * first package (mockup flow: onboard → My Services → Save package). `endAdminOnboarding`
   * clears it and switches back to the admin persona.
   */
  readonly adminOnboarding   = signal(false);

  constructor() {
    this.restoreFromStorage();
    this.bootstrapDefaultPersona();
  }

  /** True when the logged-in user holds ANY of the given role codes. */
  hasRole(...codes: string[]): boolean {
    const mine = this.roles();
    return codes.some(c => mine.includes(c));
  }

  setPersona(
    persona: Persona,
    userId: number,
    opts?: { vendorId?: number | null; customerId?: number | null; userName?: string; roles?: string[] },
  ) {
    this.currentPersona.set(persona);
    this.currentUserId.set(userId);
    this.currentVendorId.set(opts?.vendorId ?? null);
    this.currentCustomerId.set(opts?.customerId ?? null);
    if (opts?.roles) this.roles.set(opts.roles);
    // An explicit '' clears it; only `undefined` (caller has nothing to say) keeps what is there.
    if (opts?.userName !== undefined) this.currentUserName.set(opts.userName);
    this.persist();
  }

  /** Vendor-switch modal (mockup's `switchModal`) → change the managed vendor. */
  setVendor(vendorId: number) {
    this.currentVendorId.set(vendorId);
    this.persist();
  }

  /**
   * Admin onboards a vendor → temporarily act as that vendor to set up its first package.
   * Keeps the admin's userId + roles so `endAdminOnboarding` can switch straight back.
   */
  beginAdminOnboarding(vendorId: number) {
    this.adminOnboarding.set(true);
    this.setPersona('vendor', this.currentUserId() ?? 1, {
      vendorId, roles: this.roles(), userName: this.currentUserName(),
    });
  }

  /** Finished onboarding (package saved, or bailed out) → back to the admin persona. */
  endAdminOnboarding() {
    if (!this.adminOnboarding()) return;
    this.adminOnboarding.set(false);
    this.setPersona('admin', this.currentUserId() ?? 1, {
      roles: this.roles(), userName: this.currentUserName(),
    });
  }

  /** Cache the resolved customer id (e.g. looked up from the user id) so all customer
   *  pages scope to the same record — used when the JWT didn't carry customerId. */
  setCustomer(customerId: number) {
    this.currentCustomerId.set(customerId);
    this.persist();
  }

  clear() {
    this.currentPersona.set(null);
    this.currentVendorId.set(null);
    this.currentCustomerId.set(null);
    this.currentUserId.set(null);
    this.currentUserName.set('');
    this.roles.set([]);
    this.adminOnboarding.set(false);
    localStorage.removeItem(this.STORAGE_KEY);
  }

  bootstrapFromDevQueryParam() {
    if (environment.production) return;
    const params = new URLSearchParams(window.location.search);
    const dev = params.get('dev-persona');
    if (!dev) return;

    if (dev === 'vendor')        this.setPersona('vendor', 3, { vendorId: 2, roles: ['vendor_owner'] });
    else if (dev === 'admin')    this.setPersona('admin', 1, { roles: ['super_admin'] });
    else if (dev === 'customer') this.setPersona('customer', 2, { customerId: 1, roles: ['customer'] });
  }

  /** Auto-login when skipAuth is enabled — only when no persona was restored from storage. */
  bootstrapDefaultPersona() {
    if (!environment.skipAuth) return;
    if (this.currentPersona()) return;
    const persona = (environment.defaultPersona as Persona) ?? 'vendor';
    // Seed defaults: vendor id 2 = "Grand Ballroom KL" (mockup's meId), owner user id 3;
    // admin user id 1; customer id 1 = "Nurul & Hafiz".
    if (persona === 'vendor')        this.setPersona('vendor', 3, { vendorId: 2, roles: ['vendor_owner'] });
    else if (persona === 'admin')    this.setPersona('admin', 1, { roles: ['super_admin'] });
    else if (persona === 'customer') this.setPersona('customer', 2, { customerId: 1, roles: ['customer'] });
  }

  private persist() {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
      persona:    this.currentPersona(),
      vendorId:   this.currentVendorId(),
      customerId: this.currentCustomerId(),
      userId:     this.currentUserId(),
      userName:   this.currentUserName(),
      roles:      this.roles(),
      adminOnboarding: this.adminOnboarding(),
    }));
  }

  private restoreFromStorage() {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed.persona)    this.currentPersona.set(parsed.persona);
      if (parsed.vendorId)   this.currentVendorId.set(parsed.vendorId);
      if (parsed.customerId) this.currentCustomerId.set(parsed.customerId);
      if (parsed.userId)     this.currentUserId.set(parsed.userId);
      if (parsed.userName)   this.currentUserName.set(parsed.userName);
      if (Array.isArray(parsed.roles)) this.roles.set(parsed.roles);
      if (parsed.adminOnboarding) this.adminOnboarding.set(true);
    } catch {}
  }
}
